/**
 * Epic #228 P5 (design §4.2): what the merchant may do with each element of
 * a template. An element is one item path (`home.hero.slide1`) of one block.
 * Derived from the manifest only; nothing is stored, so immutable manifests
 * get capabilities without a data change.
 *
 * Not the A7 renderer registry, which lives in `capabilities.ts` (PD14).
 */
import type {
  WebsiteElementCapabilities,
  WebsiteSlotSchema,
  WebsiteTemplateManifest,
} from '../../types/website-templates';
import { WEBSITE_PAGE_KINDS } from '../../types/website-templates';
import { websiteSlotItemPath, type WebsiteEditRegionScope } from './edit-regions';
import { websiteAllowedLinkKinds } from './external-url';
import { isRetiredWebsiteSlotSemantic } from './internal';
import { websiteMediaFrame } from './media-frames';

type Destination = WebsiteElementCapabilities['destination'];

interface ElementDraft {
  readonly elementId: string;
  readonly slots: WebsiteSlotSchema[];
}

function destinationOf(
  elementId: string,
  slots: readonly WebsiteSlotSchema[],
  schemaById: ReadonlyMap<string, WebsiteSlotSchema>,
): Destination {
  // A media slot that declares `clickable` anchors that link: it wins over a
  // sibling CTA link. The manifest validator proves the target is a link of
  // the same item.
  for (const slot of slots) {
    const linkSlotId = slot.type === 'media' ? slot.clickable?.linkSlotId : undefined;
    const link = linkSlotId === undefined ? undefined : schemaById.get(linkSlotId);
    if (link?.type === 'link' && !isRetiredWebsiteSlotSemantic(link.semantic)) {
      return {
        linkSlotId: link.id,
        allowedKinds: websiteAllowedLinkKinds(link),
        anchorsMedia: true,
      };
    }
  }
  const cta = slots.find((slot) => slot.type === 'link' && slot.id === `${elementId}.ctaLink`);
  return cta
    ? { linkSlotId: cta.id, allowedKinds: websiteAllowedLinkKinds(cta), anchorsMedia: false }
    : null;
}

/**
 * The elements of one scope in manifest order (block order, then the first
 * slot of each item). An element with no editable text, media or link slot
 * is omitted, and slots with a retired semantic (epic #228 P2) are ignored.
 */
export function deriveWebsiteElementCapabilities(
  manifest: WebsiteTemplateManifest,
  scope: WebsiteEditRegionScope,
): readonly WebsiteElementCapabilities[] {
  const schemaById = new Map(manifest.slots.map((slot) => [slot.id, slot]));
  const blocks = scope === 'chrome' ? manifest.chrome : manifest.pages[scope].blocks;
  const drafts: ElementDraft[] = [];
  const byElement = new Map<string, ElementDraft>();

  for (const block of blocks) {
    for (const slotId of block.slotIds) {
      const slot = schemaById.get(slotId);
      if (!slot || isRetiredWebsiteSlotSemantic(slot.semantic)) continue;
      const elementId = websiteSlotItemPath(slotId);
      let draft = byElement.get(elementId);
      if (!draft) {
        draft = { elementId, slots: [] };
        byElement.set(elementId, draft);
        drafts.push(draft);
      }
      draft.slots.push(slot);
    }
  }

  return drafts.flatMap((draft): WebsiteElementCapabilities[] => {
    const text = draft.slots.flatMap((slot) =>
      slot.type === 'text' || slot.type === 'rich-text'
        ? [{ slotId: slot.id, kind: slot.type }]
        : [],
    );
    const media = draft.slots.flatMap((slot) => {
      if (slot.type !== 'media') return [];
      // Epic #228 P6: device and framing come from the frame registry.
      const frame = websiteMediaFrame(manifest.layoutFamily, slot.id, slot.mediaAspect);
      return [
        {
          slotId: slot.id,
          device: frame.device,
          framing: frame.perDevice ? ('per-device' as const) : ('shared' as const),
          removable: !slot.required,
          decorative: slot.decorative === true,
        },
      ];
    });
    const destination = destinationOf(draft.elementId, draft.slots, schemaById);
    if (text.length === 0 && media.length === 0 && destination === null) return [];
    return [{ elementId: draft.elementId, text, media, destination }];
  });
}

const ALL_SCOPES: readonly WebsiteEditRegionScope[] = ['chrome', ...WEBSITE_PAGE_KINDS];

/** The capabilities of the element that owns `slotId`, in any scope, or null. */
export function websiteElementCapabilitiesForSlot(
  manifest: WebsiteTemplateManifest,
  slotId: string,
): WebsiteElementCapabilities | null {
  for (const scope of ALL_SCOPES) {
    for (const element of deriveWebsiteElementCapabilities(manifest, scope)) {
      if (
        element.text.some((entry) => entry.slotId === slotId) ||
        element.media.some((entry) => entry.slotId === slotId) ||
        element.destination?.linkSlotId === slotId
      ) {
        return element;
      }
    }
  }
  return null;
}
